import AdminLayout from "../../layouts/AdminLayout";
import useAuthStore from "../../store/authStore";
import ManagerLayout from "../../layouts/ManagerLayout";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { getPendingLeaves, updateLeaveStatus } from "../../api/attendanceService";
// How often to poll (ms). 15s is fast enough to feel live without hammering the server.
const POLL_INTERVAL_MS = 15_000;

function PendingLeaveList() {
  const role = useAuthStore((state) => state.role);
  const Layout = role === "MANAGER" ? ManagerLayout : AdminLayout;

  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [toast, setToast] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);   // Date of last successful fetch
  const [secondsAgo, setSecondsAgo] = useState(0);        // For the "X secs ago" display

  // Direct document preview and direct download (no popups)
  const handleDirectPreview = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleDirectDownload = async (url, fileName) => {
    if (!url) return;
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName || "medical_document";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || "medical_document";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Tracks which months (1-12) in the selected year have pending leaves (for dot indicators)
  const [monthsWithPending, setMonthsWithPending] = useState(new Set());
  // Per-month pending count map: { 1: 3, 7: 2, ... } — built from all-year fetch
  const [monthPendingCounts, setMonthPendingCounts] = useState({});
  // Total pending count across the whole year (for default badge when no month filter)
  const [allYearPendingCount, setAllYearPendingCount] = useState(0);

  const now = new Date();
  // Default to 0 = "All Months" so the HISTORY tab shows all-year pending count
  // on first visit, before the user explicitly picks a specific month.
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [tab, setTab] = useState("PENDING");
  const [historySearchQuery, setHistorySearchQuery] = useState("");

  // Tab switch handler to reset search when leaving HISTORY
  const handleTabChange = (newTab) => {
    setTab(newTab);
    if (newTab !== "HISTORY") {
      setHistorySearchQuery("");
    }
  };

  // Filter leaves when on HISTORY tab using employee name, ID, or date
  const displayedLeaves = useMemo(() => {
    if (tab !== "HISTORY" || !historySearchQuery.trim()) {
      return leaves;
    }
    const q = historySearchQuery.trim().toLowerCase();

    return leaves.filter((leave) => {
      // 1. Employee Name
      if (leave.employeeName && leave.employeeName.toLowerCase().includes(q)) {
        return true;
      }

      // 2. Employee ID
      if (leave.employeeId && String(leave.employeeId).toLowerCase().includes(q)) {
        return true;
      }

      // 3. Date string matching (display dates: e.g. "24 Sep 2026")
      if (leave.from && leave.from.toLowerCase().includes(q)) return true;
      if (leave.to && leave.to.toLowerCase().includes(q)) return true;

      // Raw date matching (e.g. "2026-09-24")
      if (leave.fromRaw && leave.fromRaw.toLowerCase().includes(q)) return true;
      if (leave.toRaw && leave.toRaw.toLowerCase().includes(q)) return true;

      // Date variations (e.g. DD/MM/YYYY, DD-MM-YYYY, month names)
      const checkDateVariations = (dateStr) => {
        if (!dateStr) return false;
        try {
          const d = new Date(dateStr);
          if (isNaN(d.getTime())) return false;
          const day = String(d.getDate()).padStart(2, "0");
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const year = String(d.getFullYear());
          const fullMonth = d.toLocaleString("default", { month: "long" }).toLowerCase();
          const shortMonth = d.toLocaleString("default", { month: "short" }).toLowerCase();

          if (`${day}/${month}/${year}`.includes(q)) return true;
          if (`${day}-${month}-${year}`.includes(q)) return true;
          if (`${month}/${day}/${year}`.includes(q)) return true;
          if (`${year}/${month}/${day}`.includes(q)) return true;
          if (fullMonth.includes(q) || shortMonth.includes(q)) return true;
        } catch {
          // ignore parsing error
        }
        return false;
      };

      if (checkDateVariations(leave.fromRaw) || checkDateVariations(leave.toRaw)) {
        return true;
      }

      return false;
    });
  }, [leaves, tab, historySearchQuery]);

  // Keep a stable ref to the current tab/month/year so the poll callback
  // always sees the latest values without needing to be recreated.
  const tabRef = useRef(tab);
  const monthRef = useRef(selectedMonth);
  const yearRef = useRef(selectedYear);
  useEffect(() => { tabRef.current = tab; }, [tab]);
  useEffect(() => { monthRef.current = selectedMonth; }, [selectedMonth]);
  useEffect(() => { yearRef.current = selectedYear; }, [selectedYear]);

  const showToast = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

  // ── Core fetch ─────────────────────────────────────────────────────────────
  // silent=true → don't show the full skeleton loader (used for background polls)
  const loadLeaves = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const params = {};
      if (tabRef.current === "PENDING") {
        params.include_all = "false";
      } else {
        params.month = monthRef.current;
        params.year = yearRef.current;
      }
      const res = await getPendingLeaves(params);
      const mapped = (res.data.leaves || []).map((l) => ({
        id: l.id,
        employeeId: l.employee_id,
        employeeName: l.employee_name,
        from: formatDate(l.leave_date_from),
        to: formatDate(l.leave_date_to),
        fromRaw: l.leave_date_from,
        toRaw: l.leave_date_to,
        type: l.leave_type,
        typeRaw: l.leave_type_raw || l.leave_type,
        reason: l.reason,
        totalDays: l.total_days,
        managerApproval: l.manager_approval,
        managerName: l.manager,
        adminApproval: l.admin_approval,
        backupManagerApproval: l.backup_manager_approval,
        approvedByBackupManager: l.approved_by_backup_manager,
        additionalManagers: l.additional_managers || [],
        finalStatus: l.final_status,
        canApprove: l.can_approve_for_user,
        medicalDocument: l.medical_document,
        medicalDocumentName: l.medical_document_name,
      }));
      setLeaves(mapped);
      setLastUpdated(new Date());
      setSecondsAgo(0);
    } catch (err) {
      console.error("Leave fetch failed:", err.response?.data || err);
      // Don't flash an error toast on silent background polls — only on explicit loads
      if (!silent) {
        showToast(err.response?.data?.error || "Failed to load leaves", "error");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [showToast]);

  // ── Fetch all-months data to build the HISTORY badge count + month dot indicators.
  // Runs on MOUNT and whenever the year changes — NOT gated by the active tab,
  // so the badge is visible even while the user is still on the PENDING tab.
  useEffect(() => {
    let cancelled = false;
    const fetchAllMonths = async () => {
      try {
        const res = await getPendingLeaves({ month: 0, year: selectedYear });
        if (cancelled) return;
        const allLeaves = res.data.leaves || [];
        const pendingMonths = new Set();
        const perMonth = {}   // { 1: count, 2: count, ... }
        let total = 0;
        allLeaves.forEach((l) => {
          if (l.final_status === "Pending" || l.final_status === "PENDING") {
            total += 1;
            const dateStr = l.leave_date_from;
            if (dateStr) {
              const m = new Date(dateStr).getMonth() + 1;
              if (m >= 1 && m <= 12) {
                pendingMonths.add(m);
                perMonth[m] = (perMonth[m] || 0) + 1;
              }
            }
          }
        });
        setMonthsWithPending(pendingMonths);
        setMonthPendingCounts(perMonth);
        setAllYearPendingCount(total);
      } catch {
        // Silent — don't disrupt main view
      }
    };
    fetchAllMonths();
    return () => { cancelled = true; };
  }, [selectedYear]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Computed badge count for the HISTORY tab ─────────────────────────
  // • Default (PENDING tab active, or HISTORY with "All Months"): all-year total
  // • HISTORY tab + specific month selected: that month's pending count
  const historyPendingCount =
    tab === "HISTORY" && selectedMonth !== 0
      ? (monthPendingCounts[selectedMonth] || 0)
      : allYearPendingCount;

  // ── Initial load when tab/filters change (full skeleton) ──────────────────
  useEffect(() => {
    setLoading(true);
    loadLeaves(false);
  }, [tab, selectedMonth, selectedYear]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Real-time polling ─────────────────────────────────────────────────────
  useEffect(() => {
    let intervalId = null;

    const startPolling = () => {
      // Clear any existing interval before starting a new one
      if (intervalId) clearInterval(intervalId);
      intervalId = setInterval(() => {
        // Only poll when the tab is visible — no wasted requests in background
        if (!document.hidden) {
          loadLeaves(true); // silent = true: no skeleton flash
        }
      }, POLL_INTERVAL_MS);
    };

    // Immediately re-fetch when the user switches back to this browser tab
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadLeaves(true);
      }
    };

    startPolling();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loadLeaves]);

  // ── "X secs ago" ticker ───────────────────────────────────────────────────
  useEffect(() => {
    const tick = setInterval(() => {
      if (lastUpdated) {
        setSecondsAgo(Math.floor((Date.now() - lastUpdated.getTime()) / 1000));
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [lastUpdated]);

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleApprove = async (leaveId) => {
    setActionLoading((prev) => ({ ...prev, [leaveId]: "approve" }));
    try {
      await updateLeaveStatus({ leave_id: leaveId, action: "approve" });
      await loadLeaves(false); // full reload so balance is definitely fresh
      showToast("Leave approved successfully ✅");
    } catch (err) {
      const errData = err.response?.data;
      let msg = "Failed to approve leave";
      if (Array.isArray(errData)) msg = errData[0];
      else if (errData?.error) msg = errData.error;
      else if (errData?.detail) msg = errData.detail;
      else if (errData?.non_field_errors?.[0]) msg = errData.non_field_errors[0];
      else if (typeof errData === "string") msg = errData;
      showToast(msg, "error");
    } finally {
      setActionLoading((prev) => ({ ...prev, [leaveId]: null }));
    }
  };

  const handleReject = async (leaveId) => {
    setActionLoading((prev) => ({ ...prev, [leaveId]: "reject" }));
    try {
      await updateLeaveStatus({ leave_id: leaveId, action: "reject" });
      await loadLeaves(false);
      showToast("Leave rejected ❌");
    } catch (err) {
      const errData = err.response?.data;
      let msg = "Failed to reject leave";
      if (Array.isArray(errData)) msg = errData[0];
      else if (errData?.error) msg = errData.error;
      else if (typeof errData === "string") msg = errData;
      showToast(msg, "error");
    } finally {
      setActionLoading((prev) => ({ ...prev, [leaveId]: null }));
    }
  };

  const pendingCount = leaves.filter((l) => l.finalStatus === "Pending").length;

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i);

  // Human-readable "updated X ago" label
  const updatedLabel = (() => {
    if (!lastUpdated) return null;
    if (secondsAgo < 5) return "just now";
    if (secondsAgo < 60) return `${secondsAgo}s ago`;
    return `${Math.floor(secondsAgo / 60)}m ago`;
  })();

  return (
    <Layout>
      <div className="p-3 sm:p-4 md:p-6">
        <div className="w-full max-w-[1600px] mx-auto">

          {/* HEADER */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.2 }}
            className="flex items-center justify-between mb-4 sm:mb-6 gap-3 flex-wrap"
          >
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white leading-tight">
                Pending Leave List
              </h1>
              <p className="text-white/60 text-xs sm:text-sm mt-0.5">Review and manage leave requests</p>
            </div>
            <div className="text-right">
              <p className="text-white/60 text-[10px] sm:text-xs uppercase tracking-wider">Total Pending</p>
              <p className="text-3xl sm:text-4xl font-bold text-white leading-none">
                {loading ? (
                  <span className="inline-block w-10 h-9 rounded-lg bg-white/10 animate-pulse align-middle" />
                ) : pendingCount}
              </p>
              {/* Live indicator + last-updated label */}
              <div className="flex items-center justify-end gap-1.5 mt-1">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                <span className="text-white/40 text-[10px]">
                  {updatedLabel ? `Updated ${updatedLabel}` : "Loading…"}
                </span>
              </div>
            </div>
          </motion.div>

          {/* TABS & FILTERS */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 border-b border-white/10 pb-4">
            <div className="flex gap-6">
              {["PENDING", "HISTORY"].map((t) => (
                <button
                  key={t}
                  onClick={() => handleTabChange(t)}
                  className={`pb-3 px-1 text-sm font-bold transition-all relative ${tab === t ? "text-cyan-400" : "text-white/40 hover:text-white/60"}`}
                >
                  <span className="flex items-center gap-1.5">
                    {t}
                    {/* Notification badge on HISTORY tab when pending leaves exist */}
                    {t === "HISTORY" && historyPendingCount > 0 && (
                      <span
                        className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-yellow-400 text-[10px] font-bold text-yellow-900 leading-none shadow-sm"
                        title={`${historyPendingCount} pending in history`}
                      >
                        {historyPendingCount}
                      </span>
                    )}
                  </span>
                  {tab === t && (
                    <motion.div
                      layoutId="pending-leave-tab"
                      className="absolute bottom-[-17px] left-0 right-0 h-0.5 bg-cyan-400"
                    />
                  )}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {tab === "HISTORY" && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center gap-2.5 flex-wrap"
                >
                  {/* Search Bar */}
                  <div className="relative w-full sm:w-60 md:w-72">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-white/40">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      value={historySearchQuery}
                      onChange={(e) => setHistorySearchQuery(e.target.value)}
                      placeholder="Search employee, ID, date..."
                      className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-8 py-1.5 text-sm text-white placeholder-white/40 font-medium focus:outline-none focus:border-cyan-500/50 focus:bg-white/10 transition"
                    />
                    {historySearchQuery && (
                      <button
                        type="button"
                        onClick={() => setHistorySearchQuery("")}
                        className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-white/40 hover:text-white transition"
                        title="Clear search"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <select
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                      className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white font-medium focus:outline-none focus:border-cyan-500/50 transition appearance-none pr-7"
                    >
                      <option value="0" className="bg-slate-900 font-bold text-cyan-400">All Months</option>
                      {monthNames.map((name, i) => {
                        const monthNum = i + 1;
                        const hasPending = monthsWithPending.has(monthNum);
                        return (
                          <option
                            key={name}
                            value={monthNum}
                            className={`bg-slate-900 ${hasPending ? "text-yellow-400 font-bold" : ""}`}
                          >
                            {hasPending ? `${name} ●` : name}
                          </option>
                        );
                      })}
                    </select>
                    {/* Custom dropdown arrow */}
                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-white/50 text-xs">▾</span>
                  </div>
                  {/* Legend dot indicator shown beside the select when selected month has pending */}
                  {selectedMonth !== 0 && monthsWithPending.has(selectedMonth) && (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-semibold text-yellow-400 whitespace-nowrap"
                      title="This month has pending leaves"
                    >
                      <span className="w-2 h-2 rounded-full bg-yellow-400 inline-block shadow-sm shadow-yellow-400/50" />
                      Has Pending
                    </span>
                  )}
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white font-medium focus:outline-none focus:border-cyan-500/50 transition"
                  >
                    {years.map((y) => (
                      <option key={y} value={y} className="bg-slate-900">{y}</option>
                    ))}
                  </select>
                </motion.div>
              )}

              {/* Manual refresh button */}
              <button
                onClick={() => loadLeaves(false)}
                title="Refresh now"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white/60 hover:text-white hover:bg-white/10 text-xs font-medium transition-all active:scale-95"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 4v6h-6" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                </svg>
                Refresh
              </button>
            </div>
          </div>

          {/* TOAST */}
          <AnimatePresence>
            {toast && (
              <motion.div
                key="toast"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.18 }}
                className={`mb-3 px-4 py-3 rounded-xl text-sm font-semibold border flex items-start gap-2 ${toast.type === "error"
                  ? "bg-red-500/20 border-red-400/40 text-red-300"
                  : "bg-emerald-500/20 border-emerald-400/40 text-emerald-300"
                  }`}
              >
                <span className="flex-shrink-0 mt-0.5">{toast.type === "error" ? "⚠️" : "✅"}</span>
                <span>{toast.msg}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* SEARCH RESULT INDICATOR */}
          {tab === "HISTORY" && historySearchQuery.trim() && (
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-between mb-4 px-3.5 py-2 rounded-xl bg-cyan-500/10 border border-cyan-400/20 text-xs text-cyan-200"
            >
              <div className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-cyan-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span>
                  Found <strong>{displayedLeaves.length}</strong> matching {displayedLeaves.length === 1 ? "record" : "records"} for &ldquo;<span className="font-semibold text-white">{historySearchQuery}</span>&rdquo;
                </span>
              </div>
              <button
                type="button"
                onClick={() => setHistorySearchQuery("")}
                className="text-cyan-300 hover:text-white underline text-xs ml-3 font-medium transition"
              >
                Clear
              </button>
            </motion.div>
          )}

          {/* DESKTOP TABLE */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2, delay: 0.05 }}
            className="hidden lg:block bg-white/15 backdrop-blur-xl rounded-2xl overflow-hidden border border-white/20 shadow-2xl"
          >
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-white/20 border-b border-white/30 text-center">
                    {["Emp ID", "Employee", "From", "To", "Days", "Type", "Reason", "Manager", "Notified Mgr", "Admin", "Final", "Action"].map((h) => (
                      <th key={h} className="px-3 py-3.5 text-white font-bold text-xs whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <SkeletonRows cols={12} rows={5} />
                  ) : leaves.length === 0 ? (
                    <tr>
                      <td colSpan="12" className="py-14 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <span className="text-4xl opacity-40">{tab === "PENDING" ? "✅" : "📅"}</span>
                          <p className="text-white/40 text-sm font-medium">
                            {tab === "PENDING" ? "No pending leaves found" : "No leave records found for this period"}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : displayedLeaves.length === 0 ? (
                    <tr>
                      <td colSpan="12" className="py-14 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          <p className="text-white/70 text-sm font-semibold">
                            No leave records found matching &ldquo;{historySearchQuery}&rdquo;
                          </p>
                          <p className="text-white/40 text-xs">
                            Search by employee name, employee ID, or leave date
                          </p>
                          <button
                            type="button"
                            onClick={() => setHistorySearchQuery("")}
                            className="mt-2 px-3 py-1 rounded-lg bg-cyan-500/20 text-cyan-300 border border-cyan-400/30 text-xs font-medium hover:bg-cyan-500/30 transition"
                          >
                            Clear Search
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    displayedLeaves.map((leave, idx) => (
                      <motion.tr
                        key={leave.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: idx * 0.02 }}
                        className="border-b border-white/10 hover:bg-white/[0.07] text-center transition-colors"
                      >
                        <td className="px-3 py-3.5 text-white/70 font-mono text-xs">{leave.employeeId}</td>
                        <td className="px-3 py-3.5 text-white font-semibold text-left whitespace-nowrap">{leave.employeeName}</td>
                        <td className="px-3 py-3.5 text-white/80 whitespace-nowrap">{leave.from}</td>
                        <td className="px-3 py-3.5 text-white/80 whitespace-nowrap">{leave.to}</td>
                        <td className="px-3 py-3.5">
                          <DaysBadge days={leave.totalDays} type={leave.typeRaw} />
                        </td>
                        <td className="px-3 py-3.5">
                          <TypeBadge type={leave.typeRaw} label={leave.type} />
                          {leave.medicalDocument && (
                            <div className="flex items-center justify-center gap-1.5 mt-1.5">
                              <button
                                type="button"
                                onClick={() => handleDirectPreview(leave.medicalDocument)}
                                title="Preview Medical Certificate"
                                className="px-2 py-0.5 rounded-md bg-purple-500/30 hover:bg-purple-500/50 text-purple-200 text-[10px] font-semibold border border-purple-400/40 transition-colors"
                              >
                                Preview
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDirectDownload(leave.medicalDocument, leave.medicalDocumentName)}
                                title="Download Medical Certificate"
                                className="px-2 py-0.5 rounded-md bg-white/10 hover:bg-white/20 text-white/80 hover:text-white text-[10px] font-semibold border border-white/20 transition-colors"
                              >
                                Download
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3.5 text-white/70 italic text-xs max-w-[140px] truncate">{leave.reason || "—"}</td>
                        <td className="px-3 py-3.5">
                           <div className="flex flex-col items-center gap-0.5">
                             <StatusBadge status={leave.managerApproval} />
                             {leave.managerName && (
                               <span className="text-[10px] text-white/80 whitespace-nowrap">{leave.managerName}</span>
                             )}
                           </div>
                         </td>
                        <td className="px-3 py-3.5">
                          {leave.additionalManagers.length > 0 ? (
                            leave.backupManagerApproval !== "Pending" ? (
                              <div className="flex flex-col items-center gap-0.5">
                                <StatusBadge status={leave.backupManagerApproval} />
                                <span className="text-[10px] text-white/80 whitespace-nowrap">by {leave.approvedByBackupManager}</span>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center gap-0.5">
                                <StatusBadge status="Pending" />
                                <span className="text-[10px] text-white/70 max-w-[100px] truncate" title={leave.additionalManagers.join(", ")}>
                                  {leave.additionalManagers.join(", ")}
                                </span>
                              </div>
                            )
                          ) : (
                            <span className="text-white/30 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-3 py-3.5"><StatusBadge status={leave.adminApproval} /></td>
                        <td className="px-3 py-3.5"><StatusBadge status={leave.finalStatus} /></td>
                        <td className="px-3 py-3.5">
                          {leave.canApprove ? (
                            <ActionButtons
                              leave={leave}
                              onApprove={handleApprove}
                              onReject={handleReject}
                              loadingState={actionLoading[leave.id]}
                            />
                          ) : (
                            <span className="text-white/30 text-xs">—</span>
                          )}
                        </td>
                      </motion.tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* TABLET TABLE */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2, delay: 0.05 }}
            className="hidden md:block lg:hidden bg-white/15 backdrop-blur-xl rounded-2xl overflow-hidden border border-white/20 shadow-2xl"
          >
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr className="bg-white/20 border-b border-white/30 text-center">
                    {["Employee", "Dates", "Days", "Type", "Mgr", "Notified Mgr", "Admin", "Status", "Action"].map((h) => (
                      <th key={h} className="px-2 py-3 text-white font-bold whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <SkeletonRows cols={9} rows={4} />
                  ) : leaves.length === 0 ? (
                    <tr><td colSpan="9" className="py-10 text-center text-white/40">
                      {tab === "PENDING" ? "No pending leaves found" : "No leave records found for this period"}
                    </td></tr>
                  ) : displayedLeaves.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="py-10 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <p className="text-white/70 text-xs font-semibold">
                            No results matching &ldquo;{historySearchQuery}&rdquo;
                          </p>
                          <button
                            type="button"
                            onClick={() => setHistorySearchQuery("")}
                            className="px-2.5 py-1 rounded-lg bg-cyan-500/20 text-cyan-300 border border-cyan-400/30 text-[10px] font-medium hover:bg-cyan-500/30 transition"
                          >
                            Clear Search
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    displayedLeaves.map((leave, idx) => (
                      <motion.tr key={leave.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.02 }}
                        className="border-b border-white/10 hover:bg-white/[0.07] text-center transition-colors">
                        <td className="px-2 py-3 text-left">
                           <p className="text-white font-semibold text-xs">{leave.employeeName}</p>
                           <p className="text-white/40 font-mono text-[10px]">{leave.employeeId}</p>
                        </td>
                        <td className="px-2 py-3 text-white/80 whitespace-nowrap">
                          <p>{leave.from}</p>
                          {leave.from !== leave.to && <p className="text-white/50">→ {leave.to}</p>}
                        </td>
                        <td className="px-2 py-3"><DaysBadge days={leave.totalDays} type={leave.typeRaw} small /></td>
                        <td className="px-2 py-3">
                          <TypeBadge type={leave.typeRaw} label={leave.type} small />
                          {leave.medicalDocument && (
                            <div className="flex items-center justify-center gap-1 mt-1">
                              <button
                                type="button"
                                onClick={() => handleDirectPreview(leave.medicalDocument)}
                                className="px-1.5 py-0.5 rounded bg-purple-500/30 text-purple-200 text-[8px] font-medium border border-purple-400/30 hover:bg-purple-500/50"
                              >
                                Preview
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDirectDownload(leave.medicalDocument, leave.medicalDocumentName)}
                                className="px-1.5 py-0.5 rounded bg-white/10 text-white/80 text-[8px] font-medium border border-white/20 hover:bg-white/20"
                              >
                                Download
                              </button>
                            </div>
                          )}
                        </td>
                         <td className="px-2 py-3">
                           <div className="flex flex-col items-center gap-0.5">
                             <StatusBadge status={leave.managerApproval} small />
                             {leave.managerName && (
                               <span className="text-[8px] text-white/80 whitespace-nowrap">{leave.managerName}</span>
                             )}
                           </div>
                         </td>
                        <td className="px-2 py-3">
                          {leave.additionalManagers.length > 0 ? (
                            leave.backupManagerApproval !== "Pending" ? (
                              <div className="flex flex-col items-center gap-0.5">
                                <StatusBadge status={leave.backupManagerApproval} small />
                                <span className="text-[8px] text-white/80 whitespace-nowrap">by {leave.approvedByBackupManager}</span>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center gap-0.5">
                                <StatusBadge status="Pending" small />
                                <span className="text-[8px] text-white/70 max-w-[80px] truncate" title={leave.additionalManagers.join(", ")}>
                                  {leave.additionalManagers.join(", ")}
                                </span>
                              </div>
                            )
                          ) : (
                            <span className="text-white/30 text-[10px]">—</span>
                          )}
                        </td>
                        <td className="px-2 py-3"><StatusBadge status={leave.adminApproval} small /></td>
                        <td className="px-2 py-3"><StatusBadge status={leave.finalStatus} small /></td>
                        <td className="px-2 py-3">
                          {leave.canApprove ? (
                            <ActionButtons leave={leave} onApprove={handleApprove} onReject={handleReject} loadingState={actionLoading[leave.id]} compact />
                          ) : <span className="text-white/30">—</span>}
                        </td>
                      </motion.tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* MOBILE CARDS */}
          <div className="md:hidden space-y-3">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-white/10 rounded-2xl p-4 border border-white/15 space-y-3 animate-pulse">
                  <div className="flex justify-between">
                    <div className="h-4 w-28 rounded bg-white/15" />
                    <div className="h-5 w-16 rounded-full bg-white/10" />
                  </div>
                  <div className="h-3 w-20 rounded bg-white/10" />
                  <div className="grid grid-cols-2 gap-2">
                    <div className="h-3 w-full rounded bg-white/10" />
                    <div className="h-3 w-full rounded bg-white/10" />
                  </div>
                  <div className="h-9 w-full rounded-xl bg-white/10" />
                </div>
              ))
            ) : leaves.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <span className="text-5xl opacity-40">{tab === "PENDING" ? "✅" : "📅"}</span>
                <p className="text-white/40 text-sm font-medium">
                  {tab === "PENDING" ? "No pending leaves found" : "No leave records found for this period"}
                </p>
              </div>
            ) : displayedLeaves.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3 bg-white/5 rounded-2xl border border-white/10 p-6 text-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <p className="text-white/70 text-sm font-semibold">
                  No records matching &ldquo;{historySearchQuery}&rdquo;
                </p>
                <p className="text-white/40 text-xs">
                  Search by employee name, employee ID, or leave date
                </p>
                <button
                  type="button"
                  onClick={() => setHistorySearchQuery("")}
                  className="mt-1 px-3 py-1.5 rounded-lg bg-cyan-500/20 text-cyan-300 border border-cyan-400/30 text-xs font-medium hover:bg-cyan-500/30 transition"
                >
                  Clear Search
                </button>
              </div>
            ) : (
              displayedLeaves.map((leave, idx) => (
                <MobileLeaveCard
                  key={leave.id}
                  leave={leave}
                  idx={idx}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  loadingState={actionLoading[leave.id]}
                  onPreviewDoc={handleDirectPreview}
                  onDownloadDoc={handleDirectDownload}
                />
              ))
            )}
          </div>

        </div>
      </div>
    </Layout>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function MobileLeaveCard({ leave, idx, onApprove, onReject, loadingState, onPreviewDoc, onDownloadDoc }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.04, duration: 0.2 }}
      className="bg-white/15 backdrop-blur-xl rounded-2xl border border-white/20 shadow-lg overflow-hidden"
    >
      <div className="px-4 pt-4 pb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-white font-bold text-[15px] leading-tight truncate">{leave.employeeName}</p>
          <p className="text-white/45 text-xs font-mono mt-0.5">{leave.employeeId}</p>
        </div>
        <StatusBadge status={leave.finalStatus} />
      </div>

      <div className="px-4 pb-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 text-white/70 text-xs">
          <span>📅</span><span>{leave.from}</span>
          {leave.from !== leave.to && <><span className="text-white/30">→</span><span>{leave.to}</span></>}
        </div>
        <TypeBadge type={leave.typeRaw} label={leave.type} small />
        <DaysBadge days={leave.totalDays} type={leave.typeRaw} small />
      </div>

      {leave.medicalDocument && (
        <div className="px-4 pb-3 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] text-purple-300 font-semibold">
            Medical Certificate:
          </span>
          <button
            type="button"
            onClick={() => onPreviewDoc(leave.medicalDocument)}
            className="px-2.5 py-1 rounded-lg bg-purple-500/30 hover:bg-purple-500/50 text-purple-200 text-xs font-medium border border-purple-400/40 transition-colors"
          >
            Preview
          </button>
          <button
            type="button"
            onClick={() => onDownloadDoc(leave.medicalDocument, leave.medicalDocumentName)}
            className="px-2.5 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-white/90 text-xs font-medium border border-white/20 transition-colors"
          >
            Download
          </button>
        </div>
      )}

      {leave.reason && (
        <div className="px-4 pb-3">
          <p className="text-white/50 text-xs italic line-clamp-2">"{leave.reason}"</p>
        </div>
      )}

      <div className="px-4 pb-3 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-white/35 text-[10px] uppercase tracking-wider font-semibold">Mgr</span>
          <StatusBadge status={leave.managerApproval} small />
          {leave.managerName && (
            <span className="text-[10px] text-white/80">({leave.managerName})</span>
          )}
        </div>
        {leave.additionalManagers.length > 0 && (
          <div className="flex items-center gap-1.5">
            <span className="text-white/35 text-[10px] uppercase tracking-wider font-semibold">
              Notified {leave.backupManagerApproval !== "Pending" ? `(${leave.approvedByBackupManager})` : ""}
            </span>
            <StatusBadge status={leave.backupManagerApproval} small />
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <span className="text-white/35 text-[10px] uppercase tracking-wider font-semibold">Admin</span>
          <StatusBadge status={leave.adminApproval} small />
        </div>
      </div>

      {leave.canApprove && (
        <div className="px-4 pb-4">
          <ActionButtons
            leave={leave}
            onApprove={onApprove}
            onReject={onReject}
            loadingState={loadingState}
            fullWidth
          />
        </div>
      )}
    </motion.div>
  );
}

function SkeletonRows({ cols, rows }) {
  return Array.from({ length: rows }).map((_, i) => (
    <tr key={i} className="border-b border-white/[0.06]">
      {Array.from({ length: cols }).map((_, j) => (
        <td key={j} className="px-3 py-4">
          <div
            className="h-3.5 rounded-md bg-white/10 animate-pulse"
            style={{ width: j === 5 ? "90%" : j === cols - 1 ? "80%" : "65%", animationDelay: `${i * 60}ms` }}
          />
        </td>
      ))}
    </tr>
  ));
}

function StatusBadge({ status, small = false }) {
  const styles = {
    Pending: "bg-yellow-500/25 text-yellow-200 border-yellow-400/30",
    Approved: "bg-emerald-500/25 text-emerald-200 border-emerald-400/30",
    Rejected: "bg-red-500/25 text-red-200 border-red-400/30",
  };
  const sz = small ? "px-1.5 py-0.5 text-[9px]" : "px-2.5 py-1 text-xs";
  return (
    <span className={`rounded-lg border font-medium whitespace-nowrap ${sz} ${styles[status] || styles.Pending}`}>
      {status}
    </span>
  );
}

function TypeBadge({ type, label, small = false }) {
  const isCompOff = type === "COMP_OFF";
  const isMedical = type === "MEDICAL" || label?.toLowerCase().includes("medical");
  const sz = small ? "px-1.5 py-0.5 text-[9px]" : "px-2.5 py-1 text-xs";
  const cls = isCompOff
    ? "bg-violet-500/30 text-violet-200 border-violet-400/30"
    : isMedical
    ? "bg-purple-500/30 text-purple-200 border-purple-400/30"
    : "bg-blue-500/30 text-blue-200 border-blue-400/30";
  return (
    <span className={`rounded-lg border font-medium whitespace-nowrap ${sz} ${cls}`}>
      {isCompOff ? "🎁 " : ""}{label}
    </span>
  );
}

function DaysBadge({ days, type, small = false }) {
  if (days == null) return <span className="text-white/30 text-xs">—</span>;
  const num = parseFloat(days);
  const isCompOff = type === "COMP_OFF";
  const isAnomaly = isCompOff && num > 1;
  const sz = small ? "px-1.5 py-0.5 text-[9px]" : "px-2.5 py-1 text-xs";
  const cls = isAnomaly
    ? "bg-red-500/30 text-red-200 border-red-400/40"
    : "bg-white/10 text-white/70 border-white/15";
  return (
    <span className={`rounded-lg border font-medium whitespace-nowrap ${sz} ${cls}`} title={isAnomaly ? "Comp-off should be 1 day — check dates" : ""}>
      {isAnomaly ? "⚠️ " : ""}{num} {num === 1 ? "day" : "days"}
    </span>
  );
}

function ActionButtons({ leave, onApprove, onReject, loadingState, fullWidth = false, compact = false }) {
  const role = useAuthStore((state) => state.role);
  const { finalStatus, managerApproval, adminApproval, id } = leave;

  let currentStatus = finalStatus;
  if (role === "ADMIN" && adminApproval) currentStatus = adminApproval;
  if (role === "MANAGER" && managerApproval) currentStatus = managerApproval;

  const btnBase = `font-semibold rounded-xl transition-all active:scale-95 flex items-center justify-center gap-1 ${fullWidth ? "flex-1 py-3 text-xs" : compact ? "px-2 py-1.5 text-[10px]" : "w-24 py-2 text-xs"
    }`;

  if (loadingState) {
    return (
      <div className="flex justify-center py-1.5">
        <span className="w-5 h-5 rounded-full border-2 border-white/20 border-t-white/70 animate-spin" />
      </div>
    );
  }

  if (currentStatus === "Pending") {
    return (
      <div className={`flex gap-2 ${fullWidth ? "w-full" : compact ? "flex-col items-center" : "flex-col items-center py-1"}`}>
        <button type="button" onClick={(e) => { e.stopPropagation(); onApprove(id); }}
          className={`${btnBase} bg-emerald-500 hover:bg-emerald-400 text-white shadow-md`}>
          ✓ Approve
        </button>
        <button type="button" onClick={(e) => { e.stopPropagation(); onReject(id); }}
          className={`${btnBase} bg-red-500 hover:bg-red-400 text-white shadow-md`}>
          ✕ Reject
        </button>
      </div>
    );
  }
  if (currentStatus === "Approved") {
    return (
      <div className={`flex ${fullWidth ? "w-full" : "justify-center py-1"}`}>
        <button type="button" onClick={(e) => { e.stopPropagation(); onReject(id); }}
          className={`${btnBase} bg-red-500 hover:bg-red-400 text-white ${fullWidth ? "w-full" : ""}`}>
          ✕ Reject
        </button>
      </div>
    );
  }
  if (currentStatus === "Rejected") {
    return (
      <div className={`flex ${fullWidth ? "w-full" : "justify-center py-1"}`}>
        <button type="button" onClick={(e) => { e.stopPropagation(); onApprove(id); }}
          className={`${btnBase} bg-emerald-500 hover:bg-emerald-400 text-white ${fullWidth ? "w-full" : ""}`}>
          ✓ Approve
        </button>
      </div>
    );
  }
  return null;
}

export default PendingLeaveList;
